import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/UseApi';
import { showErrorMes, showSuccessMes, INFO_MESSAGES } from '../../helpers/NotificationHelper';
import { useLocation } from 'react-router-dom';
import DefaultReportsApi from '../../api/DefaultReportsApi';
import BasicTable from '../../components/table/BasicTable';
import { getTableDataFromSuiteTestCaseExecutionCountAggregation, getTableDataFromCycleUserDefectCountAggregation } from './ReportHelper';
import { SidePaneRightContent } from '../../components/Layout';
import { Heading, Button } from '../../designSystem/DesignSystem';
import { useTranslation } from 'react-i18next';
import { getReportExcelNameDate } from '../../helpers/Helper';

const DefaultReportsPage = ({ projectId, defaultReports }) => {
  const [selectedDefaultReport, setSelectedDefaultReport] = useState();
  const [tableData, setTableData] = useState([]);

  const location = useLocation();
  const selectedReportId = location.pathname.split('/')[3];
  const { t } = useTranslation();

  const cycleUserDefectCountApi = useApi({
    name: 'cycleUserDefectCount report',
    api: DefaultReportsApi.cycleUserDefectCount,
    onSuccess: () => {
      setTableData(getTableDataFromCycleUserDefectCountAggregation(cycleUserDefectCountApi?.data));
    },
    onError: (err) => showErrorMes(err)
  });

  const suiteTestCaseExecutionCountApi = useApi({
    name: 'suiteTestCaseExecutionCount report',
    api: DefaultReportsApi.suiteTestCaseExecutionCount,
    onSuccess: () => {
      setTableData(getTableDataFromSuiteTestCaseExecutionCountAggregation(suiteTestCaseExecutionCountApi?.data));
    },
    onError: (err) => showErrorMes(err)
  });

  const exportReportApi = useApi({
    name: 'export report',
    api: DefaultReportsApi.export,
    onSuccess: () => {
      downloadExportingFile(exportReportApi?.data);
    },
    onError: (err) => showErrorMes(err)
  });

  const downloadFile = () => {
    const headers = translatedColumns.map((column) => {
      let name = column.accessor;
      // Get the translated header value; if Header is a function, call it, otherwise use it directly
      const value = typeof column.Header === 'function' ? column.Header() : column.Header;
      const required = true;
      return { name, value, required };
    });

    exportReportApi.execute({ projectId, tableData, headers });
  };

  const downloadExportingFile = (data) => {
    const fileName = getReportExcelNameDate() + '_' + selectedDefaultReport?.api + '_report.xls';
    const url = window.URL.createObjectURL(new Blob([data], { entityType: 'application/vnd.ms-excel' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    showSuccessMes(INFO_MESSAGES.export);
  };

  useEffect(() => {
    setSelectedDefaultReport(defaultReports?.filter((report) => report?.id === selectedReportId)[0]);
  }, [location]);

  useEffect(() => {
    switch (selectedDefaultReport?.api) {
      case 'cycleUserDefectCount':
        cycleUserDefectCountApi?.execute({ projectId });
        break;
      case 'suiteTestCaseExecutionCount':
        suiteTestCaseExecutionCountApi?.execute({ projectId });
        break;
      default:
        setTableData([]);
        break;
    }
  }, [selectedDefaultReport]);

  const translatedColumns = (selectedDefaultReport?.columns || []).map(column => {
    const accessor = column.accessor?.includes('.') 
      ? column.accessor.split('.').pop() 
      : column.accessor;
  
    return {
      ...column,
      accessor, // updated accessor
      Header: () => t(column.Header)
    };
  });

  return (
    <>
      <SidePaneRightContent.Top>
        <Heading.Group
          type="medium"
          text={t(selectedDefaultReport?.name) || ''}
        ></Heading.Group>
      </SidePaneRightContent.Top>
      <SidePaneRightContent.Main>
        <SidePaneRightContent.Center>
          <div className="mb-2 mt-4 flex flex-col">
            <div className="flex items-start justify-start gap-2 text-primary-700">
              <div className="flex flex-row flex-wrap gap-2 gap-y-1">
                <div>
                  <Button
                    id="selectedTable-export-button"
                    tooltip={t('Export')}
                    secondary2
                    weight="bold"
                    icon={'UploadSimple'}
                    size="xsmall"
                    onClick={() => downloadFile()}
                  />
                </div>
              </div>
            </div>
          </div>
          <BasicTable
            columns={translatedColumns}
            data={tableData}
            pagination
          />
        </SidePaneRightContent.Center>
      </SidePaneRightContent.Main>
    </>
  );
};

export default DefaultReportsPage;
